import React from "react";
import { Card, List, Tag, Typography, Descriptions, Alert, Collapse, Space } from "antd";
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    InfoCircleOutlined,
    ClockCircleOutlined,
    SettingOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * 执行结果查看器组件
 * 用于显示多个操作的执行结果
 * @param {Array} executionResults - 执行结果数组
 * @param {boolean} showDetails - 是否显示详细信息
 */
const ExecutionResultsViewer = ({ executionResults = [], showDetails = true }) => {
    if (!executionResults || executionResults.length === 0) {
        return (
            <Alert 
                message="无执行结果" 
                description="此操作尚未执行或执行结果未记录。" 
                type="info" 
                showIcon 
            />
        );
    }

    // 统计执行结果
    const successCount = executionResults.filter(result => result.success).length;
    const failureCount = executionResults.length - successCount;
    const overallSuccess = failureCount === 0;

    // 渲染单个执行结果
    const renderExecutionResult = (result, index) => {
        const { success, message, command, timestamp, error } = result;
        
        return (
            <List.Item key={index}>
                <Card 
                    size="small" 
                    style={{ 
                        width: '100%',
                        borderColor: success ? '#52c41a' : '#ff4d4f',
                        backgroundColor: success ? '#f6ffed' : '#fff2f0'
                    }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* 执行状态标题 */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Space>
                                {success ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                ) : (
                                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                                )}
                                <Text strong style={{ color: success ? '#52c41a' : '#ff4d4f' }}>
                                    操作 {index + 1}: {success ? '执行成功' : '执行失败'}
                                </Text>
                            </Space>
                            {timestamp && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    <ClockCircleOutlined /> {dayjs(timestamp).format('MM-DD HH:mm:ss')}
                                </Text>
                            )}
                        </div>

                        {/* 执行消息 */}
                        {message && (
                            <Alert 
                                message={message} 
                                type={success ? "success" : "error"} 
                                showIcon 
                                style={{ margin: 0 }}
                            />
                        )}

                        {/* 错误信息 */}
                        {!success && error && (
                            <Alert 
                                message="错误详情" 
                                description={error} 
                                type="error" 
                                showIcon 
                                style={{ margin: 0 }}
                            />
                        )}

                        {/* 命令详情 */}
                        {showDetails && command && (
                            <Collapse size="small" ghost>
                                <Panel 
                                    header={
                                        <Space>
                                            <SettingOutlined />
                                            <Text type="secondary">查看执行命令详情</Text>
                                        </Space>
                                    } 
                                    key="command-details"
                                >
                                    <Descriptions size="small" column={1} bordered>
                                        {command.flight_operation_id && (
                                            <Descriptions.Item label="航班操作ID">
                                                {command.flight_operation_id}
                                            </Descriptions.Item>
                                        )}
                                        {command.change_type && (
                                            <Descriptions.Item label="变更类型">
                                                <Tag color="blue">{command.change_type}</Tag>
                                            </Descriptions.Item>
                                        )}
                                        {command.trigger_source && (
                                            <Descriptions.Item label="触发源">
                                                {command.trigger_source}
                                            </Descriptions.Item>
                                        )}
                                        {command.remarks && (
                                            <Descriptions.Item label="备注">
                                                {command.remarks}
                                            </Descriptions.Item>
                                        )}
                                        {command.payload && (
                                            <Descriptions.Item label="更新数据">
                                                <pre style={{ 
                                                    fontSize: '12px', 
                                                    backgroundColor: '#f5f5f5', 
                                                    padding: '8px', 
                                                    borderRadius: '4px',
                                                    maxHeight: '200px',
                                                    overflow: 'auto'
                                                }}>
                                                    {JSON.stringify(command.payload, null, 2)}
                                                </pre>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </Panel>
                            </Collapse>
                        )}
                    </Space>
                </Card>
            </List.Item>
        );
    };

    return (
        <div>
            {/* 执行结果概览 */}
            <Card 
                size="small" 
                style={{ 
                    marginBottom: 16,
                    borderColor: overallSuccess ? '#52c41a' : '#ff4d4f',
                    backgroundColor: overallSuccess ? '#f6ffed' : '#fff2f0'
                }}
            >
                <Space>
                    {overallSuccess ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
                    )}
                    <Title level={5} style={{ margin: 0, color: overallSuccess ? '#52c41a' : '#ff4d4f' }}>
                        执行结果概览
                    </Title>
                </Space>
                <div style={{ marginTop: 8 }}>
                    <Space>
                        <Tag color="success">成功: {successCount}</Tag>
                        {failureCount > 0 && <Tag color="error">失败: {failureCount}</Tag>}
                        <Tag color="default">总计: {executionResults.length}</Tag>
                    </Space>
                </div>
            </Card>

            {/* 详细执行结果列表 */}
            <List
                dataSource={executionResults}
                renderItem={renderExecutionResult}
                style={{ backgroundColor: '#fafafa', padding: '8px', borderRadius: '4px' }}
            />
        </div>
    );
};

export default ExecutionResultsViewer;